// Pure helpers that turn react-hook-form's flat field map into the PIF payload.
//
// Extracted from PIFForm so they can be unit-tested without rendering the form
// (which mounts dozens of TinyMCE editors). Nothing here touches React state —
// every input is passed in explicitly and every output is a new object.

// Sub-table rows carry the id of the area they belong to.
const rowsForArea = (rows, areaId) =>
  (rows || []).filter((row) => String(row.area_id) === String(areaId));

// Collapse the flat `${prefix}_${key}_${areaId}` fields into a map keyed by
// area id: { [areaId]: { key: value } }.
//
// The lazy `(.*?)` is safe because the trailing `(\d+)` can never swallow an
// underscore: `bds3_crane_operate_2_12` splits into key `crane_operate_2`,
// area `12`.
export const buildBdsMap = (formValues, prefix) => {
  const pattern = new RegExp(`^${prefix}_(.*?)(?:_(\\d+))?$`);

  return Object.entries(formValues || {}).reduce((map, [field, value]) => {
    const match = field.match(pattern);
    if (!match) return map;

    const [, key, areaId] = match;
    return { ...map, [areaId]: { ...(map[areaId] || {}), [key]: value } };
  }, {});
};

// Build the areas payload from `areaData` (the source of truth) rather than from
// the registered form fields. The Areas tab and each area's BDS use lazy
// mounting, so an area the user never opened has no registered `bds1_*` fields.
//
// Each sheet is MERGED over the area's existing values instead of replacing
// them. A field the user cleared is still present in the form map (as ""), so it
// still wins; the merge only restores keys that are ABSENT from the map — which
// is exactly the case that used to wipe a whole sheet (e.g. renaming an area
// changes every field name, leaving the map empty for that area).
export const buildAreas = (formValues, context = {}) => {
  const { areaData = [] } = context;
  const bds1Map = buildBdsMap(formValues, 'bds1');
  const bds2Map = buildBdsMap(formValues, 'bds2');
  const bds3Map = buildBdsMap(formValues, 'bds3');

  return areaData.map((area) => {
    const id = String(area.id);

    return {
      id,
      name: area.name,
      bds1: {
        ...(area.bds1 || {}),
        ...(bds1Map[id] || {}),
        open_walls: rowsForArea(context.openWalls, id),
      },
      bds2: {
        ...(area.bds2 || {}),
        ...(bds2Map[id] || {}),
        roof_extensions: rowsForArea(context.roofExtensions, id),
        canopies: rowsForArea(context.canopies, id),
        fascias: rowsForArea(context.fascias, id),
        frame_openings: rowsForArea(context.frameOpenings, id),
        mezzanines: rowsForArea(context.mezzanines, id),
        crane_informations: rowsForArea(context.craneInformations, id),
      },
      bds3: {
        ...(area.bds3 || {}),
        ...(bds3Map[id] || {}),
      },
    };
  });
};

// Paint systems and secondary members live in their own state, not in the form,
// so they are merged in here at submit time. They are deliberately NOT fed back
// into the object that seeds the GDS fields — doing so changed that object's
// identity on every row add/remove, which re-seeded every `gds.*` field from the
// server snapshot and reverted whatever the user had just typed.
export const processData = (formValues, context = {}) => ({
  gds: {
    ...(formValues?.gds || {}),
    paint_systems: context.paintSystems || [],
    secondary_members: context.secondaryMembers || [],
  },
  areas: buildAreas(formValues, context),
});

// Field names embed the area id, so renaming an area orphans every value the
// user has typed for it. Returns the `{ field: value }` pairs re-keyed to the
// new id, for the caller to feed back into react-hook-form.
export const remapAreaFieldNames = (formValues, previousAreaId, nextAreaId) => {
  if (String(previousAreaId) === String(nextAreaId)) return {};

  const suffix = `_${previousAreaId}`;

  return Object.entries(formValues || {}).reduce((moved, [field, value]) => {
    if (!field.endsWith(suffix) || !/^bds[123]_/.test(field)) return moved;

    const renamed = `${field.slice(0, -suffix.length)}_${nextAreaId}`;
    return { ...moved, [renamed]: value };
  }, {});
};
